import React, {useMemo, useState, useCallback} from 'react'
import {
  Grid,
  CircularProgress,
  Tooltip,
  IconButton,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import {format} from 'date-fns'
import _ from 'lodash'

import {
  FormProvider,
  RHFDateField,
  RHFSearchableSelectField,
} from '../../common/hook-form'
import {
  useGetAllActiveMerchantsIdAndNameCachedQuery,
  useGetAllCourierPartnersCachedQuery,
} from '../../redux/commonApiCalls'
import PageTitle from '../../components/PageTitle'
import {usePaceAnalytics} from './usePaceAnalytics'
import CopyIcon from '../../common/CopyIcon'

import SearchIcon from '@mui/icons-material/Search'
import TuneIcon from '@mui/icons-material/Tune'
import DownloadIcon from '@mui/icons-material/Download'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import PanToolOutlinedIcon from '@mui/icons-material/PanToolOutlined'
import StorefrontIcon from '@mui/icons-material/Storefront'
import PeopleIcon from '@mui/icons-material/People'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import BarChartIcon from '@mui/icons-material/BarChart'
import TableChartIcon from '@mui/icons-material/TableChart'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

import {
  PageWrapper,
  FilterCard,
  FilterHeader,
  FilterIconBox,
  FilterTitle,
  FilterBody,
  FilterHelper,
  RunBtn,
  DownloadBtn,
  KpiGrid,
  KpiCard,
  KpiTop,
  KpiLabel,
  KpiIcon,
  KpiValue,
  KpiSub,
  KpiInfoBtn,
  SplitCard,
  SplitHeader,
  SplitTitle,
  SplitBody,
  SplitBarTrack,
  SplitBarSegment,
  SplitLegend,
  LegendItem,
  LegendDot,
  LegendLabel,
  LegendValue,
  BreakdownRow,
  BreakdownChip,
  BreakdownChipLabel,
  BreakdownChipCount,
  SectionCard,
  SectionHeader,
  SectionHeaderLeft,
  SectionTitle,
  SectionBadge,
  StyledTable,
  StyledTh,
  StyledTr,
  StyledTd,
  CellBar,
  PctChip,
  TypeBadge,
  OrdersTableCard,
  PaginationRow,
  PaginationInfo,
  PaginationBtns,
  EmptyState,
  EmptyText,
  LoadingOverlay,
  DataLoadedCard,
  DataLoadedInfo,
  DataLoadedText,
  DataLoadedSub,
  DataLoadedBarTrack,
  DataLoadedBarFill,
  DataLoadedPct,
  VisualBarRow,
  VisualBarName,
  VisualBarTrack,
  VisualBarFill,
  VisualBarValue,
  WelcomeCard,
  WelcomeIconBox,
  WelcomeTitle,
  WelcomeSub,
  OrdersFilterBar,
  SearchInput,
  FilterChipBtn,
  SortTh,
  PartialBadge,
  DateChartCard,
  DayBarContainer,
  DayBarRow,
  DayLabel,
  DayBarTrack,
  DayBarFill,
  DayBarLabel,
  DayTotal,
} from './styled'

// PACE has one allocation type. Older records may still say PERFORMANCE / COST /
// BALANCED / WEIGHTED — all of them are PACE.
const PACE_TYPES = new Set(['PACE', 'PERFORMANCE', 'COST', 'BALANCED', 'WEIGHTED'])
const paceTypeLabel = (type) => (PACE_TYPES.has(String(type || '').toUpperCase()) ? 'PACE' : type)
const PACE_COLOR = {bg: '#f3e5f5', text: '#7b1fa2'}

const schema = Yup.object().shape({
  merchantId: Yup.string().optional(),
  date: Yup.string().required('Date is required'),
})

const ORDER_PAGE_SIZE = 20

function isPace(order) {
  // Primary check: allocationType is set (PACE, or a legacy PERFORMANCE/COST/BALANCED/WEIGHTED)
  if (order.allocationType != null && order.allocationType !== '') return true
  // Secondary check per API doc: apiRankedCodes is a non-empty array
  if (
    Array.isArray(order.dynamicCourierAllocationApiRankedCodes) &&
    order.dynamicCourierAllocationApiRankedCodes.length > 0
  ) return true
  return false
}

const MERCHANT_PAGE_SIZE = 10
const COURIER_PAGE_SIZE = 10

export default function PaceAnalytics() {
  const {data: merchants = []} = useGetAllActiveMerchantsIdAndNameCachedQuery()
  const activeMerchants = merchants.filter((v) => v.is_deleted === '0')

  const courierFilter = useMemo(() => ({
    andfilter: [{field: 'is_deleted', operator: 'eq', value: '0'}],
    offset: 0,
    limit: 50000,
    skipCount: true,
    includes: 'id,name,cpAccountCode,prozoId,parent',
  }), [])
  const {data: allCourierPartners} = useGetAllCourierPartnersCachedQuery(courierFilter)

  const courierParentMap = useMemo(() => {
    if (!allCourierPartners) return {}
    const byCode = {}
    const byProzoId = {}
    allCourierPartners.forEach((cp) => {
      if (cp.cpAccountCode) byCode[cp.cpAccountCode] = cp.parent || cp.name
      if (cp.prozoId) byProzoId[cp.prozoId] = cp.parent || cp.name
    })
    return {...byProzoId, ...byCode}
  }, [allCourierPartners])

  const {
    isLoading,
    summary,
    orders,
    orderCount,
    orderRemaining,
    loadData,
    loadMoreOrders,
    isLoadingMore,
    downloadReport,
    isDownloading,
  } = usePaceAnalytics()

  const [orderPage, setOrderPage] = useState(0)
  const [courierPage, setCourierPage] = useState(0)
  const [merchantView, setMerchantView] = useState('table')
  const [courierView, setCourierView] = useState('table')
  const [merchantPage, setMerchantPage] = useState(0)
  const [formParams, setFormParams] = useState(null)

  // Order search & filters
  const [orderSearch, setOrderSearch] = useState('')
  const [orderStatusFilter, setOrderStatusFilter] = useState('ALL')
  const [orderAllocFilter, setOrderAllocFilter] = useState('ALL')

  // Table sorting
  const [merchantSort, setMerchantSort] = useState({field: 'total', dir: 'desc'})
  const [courierSort, setCourierSort] = useState({field: 'total', dir: 'desc'})

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      date: format(new Date(), 'yyyy-MM-dd'),
      merchantId: '',
    },
  })
  const {handleSubmit} = methods

  const onSubmit = useCallback(
    (data) => {
      const dateStr = format(new Date(data.date), 'yyyy-MM-dd')
      const params = {
        fromDate: dateStr,
        toDate: dateStr,
        merchantId: data.merchantId || undefined,
      }
      setFormParams(params)
      setOrderPage(0)
      setCourierPage(0)
      setMerchantPage(0)
      setOrderSearch('')
      setOrderStatusFilter('ALL')
      setOrderAllocFilter('ALL')
      setMerchantSort({field: 'total', dir: 'desc'})
      setCourierSort({field: 'total', dir: 'desc'})
      loadData(params)
    },
    [loadData]
  )

  const merchantBreakdown = useMemo(() => {
    if (!orders.length) return []
    const grouped = _.groupBy(orders, 'merchantName')
    return Object.entries(grouped)
      .map(([name, items]) => {
        const pace = items.filter(isPace).length
        const manual = items.length - pace
        return {name, total: items.length, pace, manual, pacePct: Math.round((pace / items.length) * 100)}
      })
      .sort((a, b) => b.total - a.total)
  }, [orders])

  const courierBreakdown = useMemo(() => {
    if (!orders.length) return []
    const resolveParent = (order) => {
      const code = order.courierAccountCode
      if (code && courierParentMap[code]) return courierParentMap[code]
      const name = order.courierPartnerName
      if (name && courierParentMap[name]) return courierParentMap[name]
      return name || 'Unknown'
    }
    const grouped = _.groupBy(orders, resolveParent)
    return Object.entries(grouped)
      .map(([name, items]) => {
        const pace = items.filter(isPace).length
        const manual = items.length - pace
        return {name, total: items.length, pace, manual, pacePct: Math.round((pace / items.length) * 100)}
      })
      .sort((a, b) => b.total - a.total)
  }, [orders, courierParentMap])

  // Date-wise breakdown
  const dateBreakdown = useMemo(() => {
    if (!orders.length) return []
    const grouped = _.groupBy(orders, (o) => {
      if (!o.awbRegisteredDate) return 'Unknown'
      try { return format(new Date(o.awbRegisteredDate), 'yyyy-MM-dd') } catch { return 'Unknown' }
    })
    return Object.entries(grouped)
      .filter(([d]) => d !== 'Unknown')
      .map(([date, items]) => {
        const pace = items.filter(isPace).length
        return {date, dateLabel: format(new Date(date), 'dd MMM'), total: items.length, pace, manual: items.length - pace, pacePct: items.length > 0 ? Math.round((pace / items.length) * 100) : 0}
      })
      .sort((a, b) => a.date.localeCompare(b.date))
  }, [orders])

  const maxDayTotal = useMemo(() => Math.max(...dateBreakdown.map((d) => d.total), 1), [dateBreakdown])

  // Sorted breakdowns
  const sortedMerchants = useMemo(() => {
    const {field, dir} = merchantSort
    return [...merchantBreakdown].sort((a, b) => {
      if (field === 'name') return dir === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)
      return dir === 'asc' ? a[field] - b[field] : b[field] - a[field]
    })
  }, [merchantBreakdown, merchantSort])

  const sortedCouriers = useMemo(() => {
    const {field, dir} = courierSort
    return [...courierBreakdown].sort((a, b) => {
      if (field === 'name') return dir === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)
      return dir === 'asc' ? a[field] - b[field] : b[field] - a[field]
    })
  }, [courierBreakdown, courierSort])

  const pagedMerchants = useMemo(() => {
    return sortedMerchants.slice(merchantPage * MERCHANT_PAGE_SIZE, (merchantPage + 1) * MERCHANT_PAGE_SIZE)
  }, [sortedMerchants, merchantPage])
  const totalMerchantPages = Math.ceil(sortedMerchants.length / MERCHANT_PAGE_SIZE)

  const pagedCouriers = useMemo(() => {
    return sortedCouriers.slice(courierPage * COURIER_PAGE_SIZE, (courierPage + 1) * COURIER_PAGE_SIZE)
  }, [sortedCouriers, courierPage])
  const totalCourierPages = Math.ceil(sortedCouriers.length / COURIER_PAGE_SIZE)

  // Filtered orders
  const filteredOrders = useMemo(() => {
    let result = orders
    if (orderSearch) {
      const q = orderSearch.toLowerCase()
      result = result.filter((o) =>
        o.awbNumber?.toLowerCase().includes(q) ||
        o.merchantName?.toLowerCase().includes(q) ||
        o.courierPartnerName?.toLowerCase().includes(q) ||
        o.courierAccountCode?.toLowerCase().includes(q)
      )
    }
    if (orderStatusFilter !== 'ALL') {
      result = result.filter((o) => o.orderStatus === orderStatusFilter)
    }
    if (orderAllocFilter !== 'ALL') {
      result = orderAllocFilter === 'PACE' ? result.filter(isPace) : result.filter((o) => !isPace(o))
    }
    return result
  }, [orders, orderSearch, orderStatusFilter, orderAllocFilter])

  const orderStatuses = useMemo(() => {
    const statuses = new Set(orders.map((o) => o.orderStatus).filter(Boolean))
    return ['ALL', ...Array.from(statuses).sort()]
  }, [orders])

  const pagedOrders = useMemo(() => {
    return filteredOrders.slice(orderPage * ORDER_PAGE_SIZE, (orderPage + 1) * ORDER_PAGE_SIZE)
  }, [filteredOrders, orderPage])

  const totalOrderPages = Math.ceil(filteredOrders.length / ORDER_PAGE_SIZE)

  const handleMerchantSort = useCallback((field) => {
    setMerchantSort((prev) => ({field, dir: prev.field === field && prev.dir === 'desc' ? 'asc' : 'desc'}))
    setMerchantPage(0)
  }, [])

  const handleCourierSort = useCallback((field) => {
    setCourierSort((prev) => ({field, dir: prev.field === field && prev.dir === 'desc' ? 'asc' : 'desc'}))
    setCourierPage(0)
  }, [])

  const isPartialData = orderRemaining > 0
  const hasData = summary && !isLoading

  return (
    <>
      <PageTitle>PACE · Daily PACE Share</PageTitle>
      <PageWrapper>
        {/* ── Filters ── */}
        <FilterCard>
          <FilterHeader>
            <FilterIconBox><TuneIcon sx={{fontSize: 16}} /></FilterIconBox>
            <FilterTitle>Filters</FilterTitle>
          </FilterHeader>
          <FilterBody>
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2} alignItems='flex-start'>
                <Grid item xs={12} md={4}>
                  <RHFSearchableSelectField
                    name='merchantId'
                    label='Merchant (optional)'
                    options={_.orderBy(activeMerchants, 'name').map((v) => ({
                      value: v.id,
                      label: v.companyName ? `${v.name} (${v.companyName})` : v.name,
                    }))}
                    fullWidth
                    size='small'
                  />
                  <FilterHelper>Select a merchant to analyze</FilterHelper>
                </Grid>
                <Grid item xs={12} md={5}>
                  <RHFDateField name='date' label='Date *' size='small' fullWidth />
                </Grid>
                <Grid item xs={12} md={3} sx={{pt: {md: '18px !important'}}}>
                  <RunBtn
                    type='submit'
                    variant='contained'
                    color='primary'
                    size='small'
                    disabled={isLoading}
                    startIcon={isLoading ? <CircularProgress size={14} /> : <SearchIcon fontSize='small' />}
                  >
                    Analyze
                  </RunBtn>
                </Grid>
              </Grid>
            </FormProvider>
          </FilterBody>
        </FilterCard>

        {isLoading && (
          <LoadingOverlay>
            <CircularProgress />
          </LoadingOverlay>
        )}

        {!isLoading && !summary && (
          <WelcomeCard>
            <WelcomeIconBox>
              <SmartToyOutlinedIcon sx={{fontSize: 32}} />
            </WelcomeIconBox>
            <WelcomeTitle>Analyze PACE Allocation Performance</WelcomeTitle>
            <WelcomeSub>
              Select a merchant and date range above, then click Analyze to see how orders were allocated
              between PACE (automated) and manual routing.
            </WelcomeSub>
          </WelcomeCard>
        )}

        {/* ── Data Loaded Progress ── */}
        {hasData && (
          <DataLoadedCard>
            <DataLoadedInfo>
              <DataLoadedText>
                {orders.length.toLocaleString()} of {orderCount.toLocaleString()} orders loaded
              </DataLoadedText>
              <DataLoadedSub>
                {orderRemaining > 0
                  ? `${orderRemaining.toLocaleString()} remaining on server. Load more to refine merchant & courier breakdowns.`
                  : 'All matching orders loaded.'}
              </DataLoadedSub>
              <DataLoadedBarTrack>
                <DataLoadedBarFill pct={orderCount > 0 ? (orders.length / orderCount) * 100 : 0} />
              </DataLoadedBarTrack>
            </DataLoadedInfo>
            <DataLoadedPct>{orderCount > 0 ? Math.round((orders.length / orderCount) * 100) : 0}%</DataLoadedPct>
            {orderRemaining > 0 && (
              <RunBtn
                variant='outlined'
                size='small'
                disabled={isLoadingMore}
                startIcon={isLoadingMore ? <CircularProgress size={14} /> : null}
                onClick={() => formParams && loadMoreOrders(formParams)}
              >
                {isLoadingMore ? 'Loading...' : `Load ${Math.min(orderRemaining, 5000)} more`}
              </RunBtn>
            )}
            <DownloadBtn
              variant='outlined'
              color='primary'
              size='small'
              disabled={isDownloading}
              startIcon={isDownloading ? <CircularProgress size={14} /> : <DownloadIcon fontSize='small' />}
              onClick={() => formParams && downloadReport(formParams)}
            >
              Download CSV
            </DownloadBtn>
          </DataLoadedCard>
        )}

        {hasData && (
          <>
            {/* ── KPIs ── */}
            <KpiGrid>
              <KpiCard>
                <KpiTop>
                  <KpiLabel>Total Orders</KpiLabel>
                  <KpiIcon bg='#e3f2fd'><LocalShippingIcon sx={{fontSize: 14, color: '#1565c0'}} /></KpiIcon>
                </KpiTop>
                <KpiValue>{summary.totalOrders?.toLocaleString()}</KpiValue>
                <KpiSub>In selected date range</KpiSub>
              </KpiCard>
              <KpiCard>
                <KpiTop>
                  <KpiLabel>PACE Allocated</KpiLabel>
                  <KpiIcon bg='#e8f5e9'><SmartToyOutlinedIcon sx={{fontSize: 14, color: '#66bb6a'}} /></KpiIcon>
                </KpiTop>
                <KpiValue>{summary.paceAllocated?.toLocaleString()}</KpiValue>
                <KpiSub>{summary.pacePercentage}% of total</KpiSub>
              </KpiCard>
              <KpiCard>
                <KpiTop>
                  <KpiLabel>Manual Allocated</KpiLabel>
                  <KpiIcon bg='#e3f2fd'><PanToolOutlinedIcon sx={{fontSize: 14, color: '#42a5f5'}} /></KpiIcon>
                </KpiTop>
                <KpiValue>{summary.manualAllocated?.toLocaleString()}</KpiValue>
                <KpiSub>{summary.manualPercentage}% of total</KpiSub>
              </KpiCard>
              {/* <KpiCard>
                <KpiTop>
                  <KpiLabel>Merchants</KpiLabel>
                  <KpiIcon bg='#f3e5f5'><StorefrontIcon sx={{fontSize: 14, color: '#7b1fa2'}} /></KpiIcon>
                </KpiTop>
                <KpiValue>{merchantBreakdown.length}</KpiValue>
                <KpiSub>Active in this period</KpiSub>
              </KpiCard> */}
              <KpiCard>
                <KpiTop>
                  <KpiLabel>Courier Partners</KpiLabel>
                  <KpiIcon bg='#e1f5fe'><PeopleIcon sx={{fontSize: 14, color: '#0277bd'}} /></KpiIcon>
                </KpiTop>
                <KpiValue>{courierBreakdown.length}</KpiValue>
                <KpiSub>Unique couriers used</KpiSub>
              </KpiCard>
            </KpiGrid>

            {/* ── PACE vs Manual Split ── */}
            <SplitCard>
              <SplitHeader>
                <SplitTitle>Allocation Split</SplitTitle>
                <Tooltip arrow title='PACE = allocationType is set AND apiRankedCodes is non-empty. Everything else = Manual.'>
                  <KpiInfoBtn size='small'><InfoOutlinedIcon sx={{fontSize: 14}} /></KpiInfoBtn>
                </Tooltip>
              </SplitHeader>
              <SplitBody>
                <SplitBarTrack>
                  <SplitBarSegment pct={summary.pacePercentage} bg='#66bb6a' />
                  <SplitBarSegment pct={summary.manualPercentage} bg='#42a5f5' />
                </SplitBarTrack>
                <SplitLegend>
                  <LegendItem>
                    <LegendDot bg='#66bb6a' />
                    <LegendLabel>PACE Automated</LegendLabel>
                    <LegendValue>{summary.paceAllocated?.toLocaleString()} ({summary.pacePercentage}%)</LegendValue>
                  </LegendItem>
                  <LegendItem>
                    <LegendDot bg='#42a5f5' />
                    <LegendLabel>Manual</LegendLabel>
                    <LegendValue>{summary.manualAllocated?.toLocaleString()} ({summary.manualPercentage}%)</LegendValue>
                  </LegendItem>
                </SplitLegend>

                {summary.breakdown && Object.keys(summary.breakdown).length > 0 && (
                  <BreakdownRow>
                    {Object.entries(
                      Object.entries(summary.breakdown).reduce((acc, [type, count]) => {
                        const label = paceTypeLabel(type)
                        acc[label] = (acc[label] || 0) + (Number(count) || 0)
                        return acc
                      }, {})
                    ).map(([type, count]) => (
                      <BreakdownChip key={type} bg={PACE_COLOR.bg} color={PACE_COLOR.text}>
                        <BreakdownChipLabel chipColor={PACE_COLOR.text}>{type}</BreakdownChipLabel>
                        <BreakdownChipCount chipColor={PACE_COLOR.text}>{count}</BreakdownChipCount>
                      </BreakdownChip>
                    ))}
                  </BreakdownRow>
                )}
              </SplitBody>
            </SplitCard>

            {/* ── Date-wise Trend ── */}
            {dateBreakdown.length > 0 && (
              <DateChartCard>
                <SectionHeader>
                  <SectionHeaderLeft>
                    <CalendarTodayIcon sx={{fontSize: 18, color: '#1565c0'}} />
                    <SectionTitle>Day-wise Allocation Trend</SectionTitle>
                    <SectionBadge>{dateBreakdown.length} days</SectionBadge>
                    {isPartialData && (
                      <PartialBadge>
                        <WarningAmberIcon sx={{fontSize: 12}} />
                        Partial data
                      </PartialBadge>
                    )}
                  </SectionHeaderLeft>
                </SectionHeader>
                <DayBarContainer>
                  {dateBreakdown.map((day) => {
                    const barWidthPct = (day.total / maxDayTotal) * 100
                    const pacePctOfBar = day.total > 0 ? (day.pace / day.total) * 100 : 0
                    return (
                      <DayBarRow key={day.date}>
                        <DayLabel>{day.dateLabel}</DayLabel>
                        <Tooltip
                          arrow
                          title={`PACE: ${day.pace.toLocaleString()} (${day.pacePct}%) | Manual: ${day.manual.toLocaleString()} (${100 - day.pacePct}%)`}
                        >
                          <DayBarTrack sx={{position: 'relative'}}>
                            <DayBarFill pct={barWidthPct * (pacePctOfBar / 100)} bg='#66bb6a'>
                              {day.pace > 0 && barWidthPct * (pacePctOfBar / 100) > 8 && (
                                <DayBarLabel>{day.pacePct}%</DayBarLabel>
                              )}
                            </DayBarFill>
                            <DayBarFill pct={barWidthPct * ((100 - pacePctOfBar) / 100)} bg='#42a5f5' />
                          </DayBarTrack>
                        </Tooltip>
                        <DayTotal>{day.total.toLocaleString()}</DayTotal>
                      </DayBarRow>
                    )
                  })}
                  <SplitLegend sx={{pt: 1}}>
                    <LegendItem>
                      <LegendDot bg='#66bb6a' />
                      <LegendLabel>PACE</LegendLabel>
                    </LegendItem>
                    <LegendItem>
                      <LegendDot bg='#42a5f5' />
                      <LegendLabel>Manual</LegendLabel>
                    </LegendItem>
                  </SplitLegend>
                </DayBarContainer>
              </DateChartCard>
            )}

            {/* ── Merchant Breakdown (hidden when single merchant selected) ── */}
            {merchantBreakdown.length > 0 && !formParams?.merchantId && (
              <SectionCard>
                <SectionHeader>
                  <SectionHeaderLeft>
                    <StorefrontIcon sx={{fontSize: 18, color: '#7b1fa2'}} />
                    <SectionTitle>Merchant Breakdown</SectionTitle>
                    <SectionBadge>{merchantBreakdown.length}</SectionBadge>
                    {isPartialData && (
                      <PartialBadge>
                        <WarningAmberIcon sx={{fontSize: 12}} />
                        Based on {orders.length.toLocaleString()} of {orderCount.toLocaleString()} orders
                      </PartialBadge>
                    )}
                  </SectionHeaderLeft>
                  <ToggleButtonGroup
                    value={merchantView}
                    exclusive
                    onChange={(e, v) => v && setMerchantView(v)}
                    size='small'
                  >
                    <ToggleButton value='chart' sx={{px: 1, py: 0.5}}><BarChartIcon sx={{fontSize: 16}} /></ToggleButton>
                    <ToggleButton value='table' sx={{px: 1, py: 0.5}}><TableChartIcon sx={{fontSize: 16}} /></ToggleButton>
                  </ToggleButtonGroup>
                </SectionHeader>

                {merchantView === 'chart' && (
                  <SplitBody>
                    {merchantBreakdown.slice(0, 10).map((m) => (
                      <VisualBarRow key={m.name}>
                        <VisualBarName>{m.name}</VisualBarName>
                        <VisualBarTrack>
                          <VisualBarFill pct={m.pacePct} bg='#66bb6a' />
                          <VisualBarFill pct={100 - m.pacePct} bg='#42a5f5' />
                        </VisualBarTrack>
                        <VisualBarValue>{m.total.toLocaleString()}</VisualBarValue>
                      </VisualBarRow>
                    ))}
                    {merchantBreakdown.length > 10 && (
                      <Typography variant='caption' color='text.secondary'>
                        +{merchantBreakdown.length - 10} more — switch to table view
                      </Typography>
                    )}
                  </SplitBody>
                )}

                {merchantView === 'table' && (
                  <>
                    <div style={{overflowX: 'auto'}}>
                      <StyledTable>
                        <thead>
                          <tr>
                            {[{key: 'name', label: 'Merchant'}, {key: 'total', label: 'Total', align: 'center'}, {key: 'pace', label: 'PACE', align: 'center'}, {key: 'manual', label: 'Manual', align: 'center'}, {key: 'pacePct', label: 'PACE %', align: 'center'}].map((col) => (
                              <SortTh key={col.key} align={col.align} active={merchantSort.field === col.key} onClick={() => handleMerchantSort(col.key)}>
                                {col.label}
                                {merchantSort.field === col.key && (merchantSort.dir === 'asc' ? <ArrowUpwardIcon sx={{fontSize: 12, ml: 0.3, verticalAlign: 'middle'}} /> : <ArrowDownwardIcon sx={{fontSize: 12, ml: 0.3, verticalAlign: 'middle'}} />)}
                              </SortTh>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {pagedMerchants.map((m, i) => (
                            <StyledTr key={m.name} isLast={i === pagedMerchants.length - 1 && merchantPage >= totalMerchantPages - 1}>
                              <StyledTd bold>{m.name}</StyledTd>
                              <StyledTd align='center'>{m.total.toLocaleString()}</StyledTd>
                              <StyledTd align='center'>
                                {m.pace.toLocaleString()}
                                <PctChip chipBg='#e8f5e9' chipColor='#66bb6a'>{m.pacePct}%</PctChip>
                              </StyledTd>
                              <StyledTd align='center'>
                                {m.manual.toLocaleString()}
                                <PctChip chipBg='#e3f2fd' chipColor='#42a5f5'>{100 - m.pacePct}%</PctChip>
                              </StyledTd>
                              <StyledTd align='center'>
                                {m.pacePct}%
                                <CellBar pct={m.pacePct} barColor='#66bb6a' />
                              </StyledTd>
                            </StyledTr>
                          ))}
                        </tbody>
                      </StyledTable>
                    </div>
                    {merchantBreakdown.length > MERCHANT_PAGE_SIZE && (
                      <PaginationRow>
                        <PaginationInfo>
                          {merchantPage * MERCHANT_PAGE_SIZE + 1}–{Math.min((merchantPage + 1) * MERCHANT_PAGE_SIZE, merchantBreakdown.length)} of {merchantBreakdown.length}
                        </PaginationInfo>
                        <PaginationBtns>
                          <IconButton size='small' disabled={merchantPage === 0} onClick={() => setMerchantPage((p) => p - 1)}>
                            <ChevronLeftIcon fontSize='small' />
                          </IconButton>
                          <IconButton size='small' disabled={merchantPage >= totalMerchantPages - 1} onClick={() => setMerchantPage((p) => p + 1)}>
                            <ChevronRightIcon fontSize='small' />
                          </IconButton>
                        </PaginationBtns>
                      </PaginationRow>
                    )}
                  </>
                )}
              </SectionCard>
            )}

            {/* ── Courier Breakdown (grouped by parent) ── */}
            {courierBreakdown.length > 0 && (
              <SectionCard>
                <SectionHeader>
                  <SectionHeaderLeft>
                    <LocalShippingIcon sx={{fontSize: 18, color: '#1565c0'}} />
                    <SectionTitle>Courier Breakdown</SectionTitle>
                    <SectionBadge>{courierBreakdown.length} parent couriers</SectionBadge>
                    {isPartialData && (
                      <PartialBadge>
                        <WarningAmberIcon sx={{fontSize: 12}} />
                        Partial data
                      </PartialBadge>
                    )}
                  </SectionHeaderLeft>
                  <ToggleButtonGroup
                    value={courierView}
                    exclusive
                    onChange={(e, v) => v && setCourierView(v)}
                    size='small'
                  >
                    <ToggleButton value='chart' sx={{px: 1, py: 0.5}}><BarChartIcon sx={{fontSize: 16}} /></ToggleButton>
                    <ToggleButton value='table' sx={{px: 1, py: 0.5}}><TableChartIcon sx={{fontSize: 16}} /></ToggleButton>
                  </ToggleButtonGroup>
                </SectionHeader>

                {courierView === 'chart' && (
                  <SplitBody>
                    {courierBreakdown.slice(0, 10).map((c) => (
                      <VisualBarRow key={c.name}>
                        <VisualBarName>{c.name}</VisualBarName>
                        <VisualBarTrack>
                          <VisualBarFill pct={c.pacePct} bg='#66bb6a' />
                          <VisualBarFill pct={100 - c.pacePct} bg='#42a5f5' />
                        </VisualBarTrack>
                        <VisualBarValue>{c.total.toLocaleString()}</VisualBarValue>
                      </VisualBarRow>
                    ))}
                    {courierBreakdown.length > 10 && (
                      <Typography variant='caption' color='text.secondary'>
                        +{courierBreakdown.length - 10} more — switch to table view
                      </Typography>
                    )}
                  </SplitBody>
                )}

                {courierView === 'table' && (
                  <>
                    <div style={{overflowX: 'auto'}}>
                      <StyledTable>
                        <thead>
                          <tr>
                            {[{key: 'name', label: 'Courier Partner (Parent)'}, {key: 'total', label: 'Total', align: 'center'}, {key: 'pace', label: 'PACE', align: 'center'}, {key: 'manual', label: 'Manual', align: 'center'}, {key: 'pacePct', label: 'PACE %', align: 'center'}].map((col) => (
                              <SortTh key={col.key} align={col.align} active={courierSort.field === col.key} onClick={() => handleCourierSort(col.key)}>
                                {col.label}
                                {courierSort.field === col.key && (courierSort.dir === 'asc' ? <ArrowUpwardIcon sx={{fontSize: 12, ml: 0.3, verticalAlign: 'middle'}} /> : <ArrowDownwardIcon sx={{fontSize: 12, ml: 0.3, verticalAlign: 'middle'}} />)}
                              </SortTh>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {pagedCouriers.map((c, i) => (
                            <StyledTr key={c.name} isLast={i === pagedCouriers.length - 1 && courierPage >= totalCourierPages - 1}>
                              <StyledTd bold>{c.name}</StyledTd>
                              <StyledTd align='center'>{c.total.toLocaleString()}</StyledTd>
                              <StyledTd align='center'>
                                {c.pace.toLocaleString()}
                                <PctChip chipBg='#e8f5e9' chipColor='#66bb6a'>{c.pacePct}%</PctChip>
                              </StyledTd>
                              <StyledTd align='center'>
                                {c.manual.toLocaleString()}
                                <PctChip chipBg='#e3f2fd' chipColor='#42a5f5'>{100 - c.pacePct}%</PctChip>
                              </StyledTd>
                              <StyledTd align='center'>
                                {c.pacePct}%
                                <CellBar pct={c.pacePct} barColor='#66bb6a' />
                              </StyledTd>
                            </StyledTr>
                          ))}
                        </tbody>
                      </StyledTable>
                    </div>
                    {courierBreakdown.length > COURIER_PAGE_SIZE && (
                      <PaginationRow>
                        <PaginationInfo>
                          {courierPage * COURIER_PAGE_SIZE + 1}–{Math.min((courierPage + 1) * COURIER_PAGE_SIZE, courierBreakdown.length)} of {courierBreakdown.length}
                        </PaginationInfo>
                        <PaginationBtns>
                          <IconButton size='small' disabled={courierPage === 0} onClick={() => setCourierPage((p) => p - 1)}>
                            <ChevronLeftIcon fontSize='small' />
                          </IconButton>
                          <IconButton size='small' disabled={courierPage >= totalCourierPages - 1} onClick={() => setCourierPage((p) => p + 1)}>
                            <ChevronRightIcon fontSize='small' />
                          </IconButton>
                        </PaginationBtns>
                      </PaginationRow>
                    )}
                  </>
                )}
              </SectionCard>
            )}

            {/* ── Orders Table ── */}
            <OrdersTableCard>
              <SectionHeader>
                <SectionHeaderLeft>
                  <SectionTitle>Order-level Details</SectionTitle>
                  <SectionBadge>{filteredOrders.length.toLocaleString()} shown / {orders.length.toLocaleString()} loaded / {orderCount.toLocaleString()} total</SectionBadge>
                </SectionHeaderLeft>
              </SectionHeader>
              <OrdersFilterBar>
                <SearchInput
                  placeholder='Search AWB, merchant, courier...'
                  value={orderSearch}
                  onChange={(e) => { setOrderSearch(e.target.value); setOrderPage(0) }}
                />
                <FilterChipBtn active={orderAllocFilter === 'ALL'} onClick={() => { setOrderAllocFilter('ALL'); setOrderPage(0) }}>All</FilterChipBtn>
                <FilterChipBtn active={orderAllocFilter === 'PACE'} onClick={() => { setOrderAllocFilter('PACE'); setOrderPage(0) }}>PACE</FilterChipBtn>
                <FilterChipBtn active={orderAllocFilter === 'MANUAL'} onClick={() => { setOrderAllocFilter('MANUAL'); setOrderPage(0) }}>Manual</FilterChipBtn>
                <Typography variant='caption' color='text.secondary' sx={{mx: 0.5}}>|</Typography>
                {orderStatuses.map((s) => (
                  <FilterChipBtn key={s} active={orderStatusFilter === s} onClick={() => { setOrderStatusFilter(s); setOrderPage(0) }}>
                    {s === 'ALL' ? 'All Statuses' : s}
                  </FilterChipBtn>
                ))}
              </OrdersFilterBar>
              <div style={{overflowX: 'auto'}}>
                <StyledTable>
                  <thead>
                    <tr>
                      <StyledTh>AWB</StyledTh>
                      {!formParams?.merchantId && <StyledTh>Merchant</StyledTh>}
                      <StyledTh>Courier</StyledTh>
                      <StyledTh align='center'>Allocation</StyledTh>
                      <StyledTh align='center'>Status</StyledTh>
                      <StyledTh>Registered Date</StyledTh>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedOrders.length === 0 ? (
                      <tr>
                        <td colSpan={formParams?.merchantId ? 5 : 6}>
                          <EmptyState><EmptyText>No orders to display</EmptyText></EmptyState>
                        </td>
                      </tr>
                    ) : (
                      pagedOrders.map((o, i) => {
                        const pace = isPace(o)
                        return (
                          <StyledTr key={o.awbNumber || i} isLast={i === pagedOrders.length - 1}>
                            <StyledTd bold style={{fontFamily: 'monospace', fontSize: '0.75rem'}}>{o.awbNumber}<CopyIcon text={o.awbNumber} /></StyledTd>
                            {!formParams?.merchantId && <StyledTd>{o.merchantName}</StyledTd>}
                            <StyledTd>{(o.courierAccountCode && courierParentMap[o.courierAccountCode]) || courierParentMap[o.courierPartnerName] || o.courierPartnerName}</StyledTd>
                            <StyledTd align='center'>
                              {pace ? (
                                <TypeBadge bg={PACE_COLOR.bg} textColor={PACE_COLOR.text}>
                                  {paceTypeLabel(o.allocationType) || 'PACE'}
                                </TypeBadge>
                              ) : (
                                <TypeBadge bg='#e3f2fd' textColor='#42a5f5'>Manual</TypeBadge>
                              )}
                            </StyledTd>
                            <StyledTd align='center'>
                              <TypeBadge
                                bg={o.orderStatus === 'DELIVERED' ? '#e8f5e9' : o.orderStatus === 'RTO' ? '#ffebee' : '#e3f2fd'}
                                textColor={o.orderStatus === 'DELIVERED' ? '#2e7d32' : o.orderStatus === 'RTO' ? '#c62828' : '#1565c0'}
                              >
                                {o.orderStatus}
                              </TypeBadge>
                            </StyledTd>
                            <StyledTd>
                              {o.awbRegisteredDate
                                ? format(new Date(o.awbRegisteredDate), 'dd MMM yyyy, HH:mm')
                                : '-'}
                            </StyledTd>
                          </StyledTr>
                        )
                      })
                    )}
                  </tbody>
                </StyledTable>
              </div>
              {filteredOrders.length > ORDER_PAGE_SIZE && (
                <PaginationRow>
                  <PaginationInfo>
                    {orderPage * ORDER_PAGE_SIZE + 1}–{Math.min((orderPage + 1) * ORDER_PAGE_SIZE, filteredOrders.length)} of {filteredOrders.length}
                    {orderRemaining > 0 && ` (${orderRemaining.toLocaleString()} more on server)`}
                  </PaginationInfo>
                  <PaginationBtns>
                    <IconButton size='small' disabled={orderPage === 0} onClick={() => setOrderPage((p) => p - 1)}>
                      <ChevronLeftIcon fontSize='small' />
                    </IconButton>
                    <IconButton size='small' disabled={orderPage >= totalOrderPages - 1} onClick={() => setOrderPage((p) => p + 1)}>
                      <ChevronRightIcon fontSize='small' />
                    </IconButton>
                  </PaginationBtns>
                </PaginationRow>
              )}
            </OrdersTableCard>
          </>
        )}
      </PageWrapper>
    </>
  )
}
